import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, CardService, ConfirmationComponent, Funnel, FunnelCategory, FunnelCategoryService, FunnelService } from 'lib-trend-core';
import { EMPTY, finalize, switchMap, takeUntil } from 'rxjs';
import { CategoryQuickFormComponent } from '../category-quick-form/category-quick-form.component';

@Component({
    selector: 'app-funnel-form',
    templateUrl: './funnel-form.component.html',
    styleUrl: './funnel-form.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FunnelFormComponent extends AbstractComponent {
  public funnel: Funnel;
  public funnelCategoryList: FunnelCategory[] = [];
  public funnelCategory: FunnelCategory;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<FunnelFormComponent>,
    private funnelService: FunnelService,
    private cardService: CardService,
    private funnelCategoryService: FunnelCategoryService,
    @Inject(MAT_DIALOG_DATA) public data: Funnel,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.isNew = !this.data
    this.getFunnelCategoryList();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [this.data?.name ?? '', [Validators.required]],
      description: [this.data?.description ?? ''],
      category: [this.data?.category?._id ?? null, [Validators.required]],
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const rawVelue = this.formGroup.getRawValue();
    this.funnel = { ...rawVelue, company: super.getCompanyCurrentUser(), };

    const saveObservable = this.isNew
      ? this.funnelService.create(this.funnel)
      : this.funnelService.update(this.data._id, this.funnel);

    saveObservable.pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe({
        next: (funnel: Funnel) => {
          this.dialogRef.close(funnel);
        },
        error: (error) => {
          this.alertService.error(error);
        },
        complete: () => {
          this.alertService.success(this.t('kanban.funnelForm.saveSuccess'));
        }
      });
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent);

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$),
      switchMap(confirmed => {
        if (!confirmed) {
          return EMPTY;
        }

        return this.cardService.getCardsByFunnel(this.data._id).pipe(
          switchMap(cards => {
            if (cards && cards.length > 0) {
              this.alertService.info(this.t('kanban.funnelForm.deleteWithCardsError'));
              return EMPTY;
            }

            return this.funnelService.delete(this.data._id);
          })
        );
      })
    ).subscribe({
      next: () => {
        this.alertService.success(this.t('kanban.funnelForm.deleteSuccess'));
        this.dialogRef.close({ action: 'delete', deleted: true });
      },
      error: (error) => {
        this.alertService.error(error);
      }
    });
  }

  createCategory(): void {
    const dialogRef = this.dialog.open(CategoryQuickFormComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((category: FunnelCategory) => {
      if (category) {
        this.getFunnelCategoryList();
      }
    });
  }

  getFunnelCategoryList() {
    this.funnelCategoryService.getList().pipe(takeUntil(this.destroy$)).subscribe({
      next: (categories: FunnelCategory[]) => {
        this.funnelCategoryList = categories;
      },
      error: (error) => {
        this.alertService.error(error);
      }
    });
  }
}
